import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Dropdown, Form, InputGroup, OverlayTrigger, ProgressBar, Table, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Pencil, Search, Trash2 } from 'lucide-react';
import Modal_proyectos from '../menus/Modal';
import {
  create_subsystem_version,
  delete_subsystem_version,
  get_subsystem_versions,
  update_subsystem_version,
} from '../../utils/services';

const DESCRIPTION_PREVIEW_MAX = 80;
const PACKAGE_TYPES = [
  { value: 'subsystem', label: 'Subsistema', color: '#0d6efd', rowBg: 'rgba(13, 110, 253, 0.08)' },
  { value: 'tomi8_package', label: 'Paquete TOMi8', color: '#198754', rowBg: 'rgba(25, 135, 84, 0.08)' },
];

const EMPTY_FORM = {
  package_name: '',
  description: '',
  type: 'subsystem',
  shortcut_label: '',
  shortcut_local_url: '',
  reserved_port: '',
  license_app_id: '',
  shortcut_icon: null,
  file: null,
};

const SORTABLE_COLUMNS = {
  package_name: 'Paquete',
  shortcut_label: 'Acceso directo',
  shortcut_local_url: 'URL local',
  reserved_port: 'Puerto reservado',
  license_app_id: 'ID licencia',
  description: 'Descripción',
  publish_date: 'Fecha publicación',
};

const TABLE_COLUMN_COUNT = Object.keys(SORTABLE_COLUMNS).length + 1;
const SIGNED_INT_MIN = -2147483648;
const SIGNED_INT_MAX = 2147483647;

function truncateDescription(text, max = DESCRIPTION_PREVIEW_MAX) {
  const value = String(text ?? '');
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max)}…`;
}

function isValidArchiveFile(file) {
  if (!file) {
    return false;
  }
  const lower = file.name.toLowerCase();
  return lower.endsWith('.tar.gz') || lower.endsWith('.tgz');
}

function isValidIconFile(file) {
  if (!file) {
    return false;
  }
  const allowed = ['image/png', 'image/jpeg', 'image/webp'];
  if (allowed.includes(file.type)) {
    return true;
  }
  const lower = file.name.toLowerCase();
  return lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp');
}

function checkSquareImage(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.width === img.height);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };
    img.src = url;
  });
}

function parsePublishDate(dateValue) {
  if (!dateValue) {
    return null;
  }
  const parsed = new Date(String(dateValue).replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
}

function formatPublishDateShort(dateValue) {
  const parsed = parsePublishDate(dateValue);
  if (!parsed) {
    return dateValue || '';
  }
  return parsed.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatPublishDateFull(dateValue) {
  const parsed = parsePublishDate(dateValue);
  if (!parsed) {
    return dateValue || '';
  }
  return parsed.toLocaleString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function getTypeMeta(type) {
  return PACKAGE_TYPES.find((item) => item.value === type) || null;
}

function getRowStyleForType(type) {
  const meta = getTypeMeta(type);
  if (!meta) {
    return undefined;
  }
  return {
    backgroundColor: meta.rowBg,
    boxShadow: `inset 4px 0 0 ${meta.color}`,
  };
}

function TypeColorSwatch({ color, size = 14 }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: 3,
        backgroundColor: color,
        border: '1px solid rgba(0,0,0,0.15)',
        flexShrink: 0,
      }}
    />
  );
}

function optionalIntToInput(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
}

function normalizeOptionalInt(value) {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') {
    return { ok: true, value: '' };
  }
  if (!/^-?\d+$/.test(trimmed)) {
    return { ok: false, value: '' };
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed) || parsed < SIGNED_INT_MIN || parsed > SIGNED_INT_MAX) {
    return { ok: false, value: '' };
  }
  return { ok: true, value: String(parsed) };
}

function getSortValue(version, column) {
  if (column === 'publish_date') {
    const parsed = new Date(String(version.publish_date ?? '').replace(' ', 'T'));
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }
  if (column === 'reserved_port' || column === 'license_app_id') {
    const parsed = Number(version[column]);
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  }
  return String(version[column] ?? '').toLowerCase();
}

function SubsistemaTOMi() {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [versions, setVersions] = useState([]);
  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('publish_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [currentUrl, setCurrentUrl] = useState('');
  const [currentIconUrl, setCurrentIconUrl] = useState('');
  const [iconPreviewUrl, setIconPreviewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await get_subsystem_versions(token);
      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      if (result === null) {
        setVersions([]);
        return;
      }
      if (!Array.isArray(result)) {
        setVersions([]);
        setError('No se pudieron cargar las versiones del subsistema.');
        return;
      }
      setVersions(result);
    } catch (err) {
      setVersions([]);
      setError('Error al cargar las versiones del subsistema.');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  useEffect(() => {
    if (!formValues.shortcut_icon) {
      setIconPreviewUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(formValues.shortcut_icon);
    setIconPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [formValues.shortcut_icon]);

  const filteredVersions = useMemo(() => {
    const nameTerm = nameFilter.trim().toLowerCase();
    const filtered = versions.filter((version) => {
      const matchesName =
        !nameTerm ||
        String(version.package_name ?? '').toLowerCase().includes(nameTerm) ||
        String(version.description ?? '').toLowerCase().includes(nameTerm) ||
        String(version.shortcut_label ?? '').toLowerCase().includes(nameTerm) ||
        String(version.shortcut_local_url ?? '').toLowerCase().includes(nameTerm) ||
        String(version.reserved_port ?? '').toLowerCase().includes(nameTerm) ||
        String(version.license_app_id ?? '').toLowerCase().includes(nameTerm);
      const matchesType = !typeFilter || version.type === typeFilter;
      return matchesName && matchesType;
    });

    const sorted = [...filtered].sort((a, b) => {
      const valueA = getSortValue(a, sortColumn);
      const valueB = getSortValue(b, sortColumn);
      if (valueA < valueB) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [versions, nameFilter, typeFilter, sortColumn, sortDirection]);

  const resetForm = () => {
    setFormValues(EMPTY_FORM);
    setCurrentVersionId(null);
    setCurrentUrl('');
    setCurrentIconUrl('');
    setIconPreviewUrl('');
    setIsEditing(false);
    setUploadProgress(null);
  };

  const handleUploadProgress = (progress) => {
    setUploadProgress({
      loaded: progress.loaded,
      total: progress.total,
      percent: progress.percent,
      phase: progress.percent >= 100 ? 'processing' : 'uploading',
    });
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (version) => {
    setFormValues({
      package_name: version.package_name || '',
      description: version.description || '',
      type: version.type || 'subsystem',
      shortcut_label: version.shortcut_label || '',
      shortcut_local_url: version.shortcut_local_url || '',
      reserved_port: optionalIntToInput(version.reserved_port),
      license_app_id: optionalIntToInput(version.license_app_id),
      shortcut_icon: null,
      file: null,
    });
    setCurrentVersionId(version.id);
    setCurrentUrl(version.url || '');
    setCurrentIconUrl(version.shortcut_icon_url || '');
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleChange = (field) => async (event) => {
    if (field === 'file') {
      setFormValues((prev) => ({ ...prev, file: event.target.files?.[0] ?? null }));
      return;
    }
    if (field === 'shortcut_icon') {
      const file = event.target.files?.[0] ?? null;
      if (file) {
        if (!isValidIconFile(file)) {
          alert('El icono debe ser PNG, JPG o WebP.');
          event.target.value = '';
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          alert('El icono no puede superar 2 MB.');
          event.target.value = '';
          return;
        }
        const isSquare = await checkSquareImage(file);
        if (!isSquare) {
          const proceed = window.confirm(
            'La imagen no es cuadrada. Se recomienda un icono cuadrado. ¿Continuar de todos modos?'
          );
          if (!proceed) {
            event.target.value = '';
            return;
          }
        }
      }
      setFormValues((prev) => ({ ...prev, shortcut_icon: file }));
      return;
    }
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'publish_date' ? 'desc' : 'asc');
  };

  const sortIndicator = (column) => {
    if (sortColumn !== column) {
      return '';
    }
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formValues.package_name.trim() || !formValues.description.trim() || !formValues.type) {
      return;
    }

    if (!isEditing && !formValues.file) {
      return;
    }

    if (formValues.file && !isValidArchiveFile(formValues.file)) {
      alert('El archivo debe ser .tar.gz o .tgz');
      return;
    }

    const reservedPort = normalizeOptionalInt(formValues.reserved_port);
    if (!reservedPort.ok) {
      alert('El puerto reservado debe ser un número entero.');
      return;
    }

    const licenseAppId = normalizeOptionalInt(formValues.license_app_id);
    if (!licenseAppId.ok) {
      alert('El ID de licencia debe ser un número entero.');
      return;
    }

    setSaving(true);
    const hasUpload = Boolean(formValues.file || formValues.shortcut_icon);
    setUploadProgress(
      formValues.file
        ? { percent: 0, loaded: 0, total: formValues.file.size, phase: 'uploading' }
        : hasUpload
          ? { percent: 0, loaded: 0, total: formValues.shortcut_icon?.size || 1, phase: 'uploading' }
          : null
    );
    try {
      const onProgress = hasUpload ? handleUploadProgress : undefined;
      const response = isEditing
        ? await update_subsystem_version(
            token,
            currentVersionId,
            formValues.package_name.trim(),
            formValues.description.trim(),
            formValues.type,
            formValues.file,
            onProgress,
            formValues.shortcut_label.trim(),
            formValues.shortcut_icon,
            formValues.shortcut_local_url.trim(),
            reservedPort.value,
            licenseAppId.value
          )
        : await create_subsystem_version(
            token,
            formValues.package_name.trim(),
            formValues.description.trim(),
            formValues.type,
            formValues.file,
            onProgress,
            formValues.shortcut_label.trim(),
            formValues.shortcut_icon,
            formValues.shortcut_local_url.trim(),
            reservedPort.value,
            licenseAppId.value
          );

      if (response === 'INVALID') {
        navigate('/');
        return;
      }

      if (response?.status === 'error') {
        alert(response.message || 'No se pudo guardar la versión.');
        return;
      }

      await fetchVersions();
      resetForm();
      setModalOpen(false);
    } catch (err) {
      alert(err?.message || 'Error al subir el archivo.');
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  const handleDelete = async (versionId) => {
    const confirmed = window.confirm('¿Eliminar esta versión del subsistema?');
    if (!confirmed) {
      return;
    }

    const response = await delete_subsystem_version(token, versionId);
    if (response === 'INVALID') {
      navigate('/');
      return;
    }
    if (response?.status === 'error') {
      alert(response.message || 'No se pudo eliminar la versión.');
      return;
    }
    await fetchVersions();
  };

  const displayedIconPreview = iconPreviewUrl || currentIconUrl;
  const selectedTypeFilter = getTypeMeta(typeFilter);

  return (
    <Container fluid className="py-4">
      <Modal_proyectos
        isOpen={isModalOpen}
        onClose={() => {
          if (saving) {
            return;
          }
          setModalOpen(false);
          resetForm();
        }}
        title={isEditing ? 'Editar versión' : 'Agregar versión'}
      >
        <form onSubmit={handleSubmit} className="d-flex flex-column h-100">
          <div className="modal-inner" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {uploadProgress && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small fw-semibold">
                    {uploadProgress.phase === 'processing'
                      ? 'Procesando en el servidor…'
                      : 'Subiendo archivo…'}
                  </span>
                  <span className="small text-muted">
                    {uploadProgress.phase === 'uploading'
                      ? `${uploadProgress.percent}% · ${formatBytes(uploadProgress.loaded)} / ${formatBytes(uploadProgress.total)}`
                      : 'Guardando archivos'}
                  </span>
                </div>
                <ProgressBar
                  now={uploadProgress.phase === 'processing' ? 100 : uploadProgress.percent}
                  animated={uploadProgress.phase === 'processing'}
                  striped={uploadProgress.phase === 'processing'}
                  label={uploadProgress.phase === 'uploading' ? `${uploadProgress.percent}%` : undefined}
                />
              </div>
            )}
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-package-name">
                Nombre de paquete
              </label>
              <input
                id="subsystem-package-name"
                type="text"
                className="form-control"
                value={formValues.package_name}
                onChange={handleChange('package_name')}
                maxLength={100}
                required
                disabled={saving}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-type">
                Tipo de paquete
              </label>
              <select
                id="subsystem-type"
                className="form-select"
                value={formValues.type}
                onChange={handleChange('type')}
                required
                disabled={saving}
              >
                {PACKAGE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-shortcut-label">
                Texto acceso directo
              </label>
              <input
                id="subsystem-shortcut-label"
                type="text"
                className="form-control"
                value={formValues.shortcut_label}
                onChange={handleChange('shortcut_label')}
                maxLength={100}
                placeholder="Nombre corto en el launcher"
                disabled={saving}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-shortcut-local-url">
                URL local del acceso directo
              </label>
              <input
                id="subsystem-shortcut-local-url"
                type="text"
                className="form-control"
                value={formValues.shortcut_local_url}
                onChange={handleChange('shortcut_local_url')}
                maxLength={500}
                placeholder="Ej. /opt/tomi/apps/mi-app o file:///..."
                disabled={saving}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-reserved-port">
                Puerto reservado
              </label>
              <input
                id="subsystem-reserved-port"
                type="number"
                className="form-control"
                value={formValues.reserved_port}
                onChange={handleChange('reserved_port')}
                step="1"
                placeholder="Opcional"
                disabled={saving}
              />
              <div className="form-text">Opcional. Número entero.</div>
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-license-app-id">
                ID de licencia de la app
              </label>
              <input
                id="subsystem-license-app-id"
                type="number"
                className="form-control"
                value={formValues.license_app_id}
                onChange={handleChange('license_app_id')}
                step="1"
                placeholder="Opcional"
                disabled={saving}
              />
              <div className="form-text">Opcional. Número entero.</div>
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-shortcut-icon">
                Icono acceso directo
              </label>
              <input
                id="subsystem-shortcut-icon"
                type="file"
                className="form-control"
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                onChange={handleChange('shortcut_icon')}
                disabled={saving}
              />
              <div className="form-text">PNG, JPG o WebP. Preferible cuadrado. Máx. 2 MB.</div>
              {isEditing && !formValues.shortcut_icon && (
                <div className="form-text">Deja vacío para mantener el icono actual.</div>
              )}
              {displayedIconPreview && (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <img
                    src={displayedIconPreview}
                    alt="Vista previa del icono"
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #dee2e6',
                    }}
                  />
                  <span className="small text-muted">Vista previa</span>
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-description">
                Descripción
              </label>
              <textarea
                id="subsystem-description"
                className="form-control"
                rows={3}
                value={formValues.description}
                onChange={handleChange('description')}
                required
                disabled={saving}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-file">
                Archivo (.tar.gz)
              </label>
              <input
                id="subsystem-file"
                type="file"
                className="form-control"
                accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
                onChange={handleChange('file')}
                required={!isEditing}
                disabled={saving}
              />
              {formValues.file && (
                <div className="form-text">
                  Tamaño seleccionado: {formatBytes(formValues.file.size)}
                </div>
              )}
              {isEditing && (
                <div className="form-text">
                  Deja vacío para mantener el archivo actual.
                </div>
              )}
            </div>
            {isEditing && currentUrl && (
              <div className="mb-3">
                <label className="form-label">URL actual</label>
                <input type="text" className="form-control" value={currentUrl} readOnly />
              </div>
            )}
          </div>
          <div className="modal-footer-fixed">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? uploadProgress?.phase === 'processing'
                  ? 'Procesando…'
                  : uploadProgress
                    ? `Subiendo… ${uploadProgress.percent}%`
                    : 'Guardando…'
                : isEditing
                  ? 'Guardar cambios'
                  : 'Agregar versión'}
            </button>
          </div>
        </form>
      </Modal_proyectos>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="mb-1">Subsistema TOMi</h2>
          <p className="text-muted mb-0">Administra las versiones del subsistema Debian.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreateModal}>
          Agregar versión
        </button>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-3">
        <InputGroup style={{ maxWidth: '320px' }}>
          <InputGroup.Text>
            <Search size={16} />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Filtrar por nombre…"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
          />
        </InputGroup>
        <Dropdown onSelect={(value) => setTypeFilter(value ?? '')}>
          <Dropdown.Toggle
            variant="outline-secondary"
            id="subsystem-type-filter"
            className="d-flex align-items-center gap-2"
            style={{ maxWidth: '260px' }}
          >
            {selectedTypeFilter ? (
              <>
                <TypeColorSwatch color={selectedTypeFilter.color} />
                <span>{selectedTypeFilter.label}</span>
              </>
            ) : (
              <span>Todos los tipos</span>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ minWidth: '240px' }}>
            <Dropdown.Item
              eventKey=""
              active={!typeFilter}
              className="d-flex align-items-center gap-2"
            >
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  border: '1px solid #adb5bd',
                  background:
                    'linear-gradient(135deg, #0d6efd 50%, #198754 50%)',
                  flexShrink: 0,
                }}
              />
              Todos los tipos
            </Dropdown.Item>
            {PACKAGE_TYPES.map((option) => (
              <Dropdown.Item
                key={option.value}
                eventKey={option.value}
                active={typeFilter === option.value}
                className="d-flex align-items-center gap-2"
              >
                <TypeColorSwatch color={option.color} />
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                {Object.entries(SORTABLE_COLUMNS).map(([column, label]) => (
                  <th
                    key={column}
                    role="button"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort(column)}
                    title={`Ordenar por ${label}`}
                  >
                    {label}
                    {sortIndicator(column)}
                  </th>
                ))}
                <th className="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={TABLE_COLUMN_COUNT} className="text-center text-muted py-4">
                    Cargando versiones…
                  </td>
                </tr>
              )}
              {!loading && filteredVersions.length === 0 && (
                <tr>
                  <td colSpan={TABLE_COLUMN_COUNT} className="text-center text-muted py-4">
                    No hay versiones registradas.
                  </td>
                </tr>
              )}
              {!loading &&
                filteredVersions.map((version) => (
                  <tr key={version.id} style={getRowStyleForType(version.type)}>
                    <td className="fw-semibold">{version.package_name}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {version.shortcut_icon_url ? (
                          <img
                            src={version.shortcut_icon_url}
                            alt={version.shortcut_label || version.package_name}
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #dee2e6',
                            }}
                          />
                        ) : (
                          <span
                            className="d-inline-block bg-light border rounded"
                            style={{ width: 32, height: 32 }}
                          />
                        )}
                        <span>{version.shortcut_label || '-'}</span>
                      </div>
                    </td>
                    <td>
                      <span className="text-truncate d-inline-block" style={{ maxWidth: '180px' }} title={version.shortcut_local_url || ''}>
                        {version.shortcut_local_url || '-'}
                      </span>
                    </td>
                    <td>{optionalIntToInput(version.reserved_port) || '-'}</td>
                    <td>{optionalIntToInput(version.license_app_id) || '-'}</td>
                    <td title={version.description || ''}>
                      {truncateDescription(version.description)}
                    </td>
                    <td>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`publish-date-${version.id}`}>
                            {formatPublishDateFull(version.publish_date)}
                          </Tooltip>
                        }
                      >
                        <span style={{ cursor: 'default', borderBottom: '1px dotted #adb5bd' }}>
                          {formatPublishDateShort(version.publish_date)}
                        </span>
                      </OverlayTrigger>
                    </td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openEditModal(version)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(version.id)}
                          aria-label="Eliminar"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </div>
    </Container>
  );
}

export default SubsistemaTOMi;
